import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { channelsService, ChannelDiscovery } from '../../services/channels.service'
import { getErrorMessage } from '../../types'
import toast from 'react-hot-toast'

interface ChannelDiscoveryModalProps {
  onClose: () => void
}

export default function ChannelDiscoveryModal({ onClose }: ChannelDiscoveryModalProps) {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [minSubscribers, setMinSubscribers] = useState(0)
  const [results, setResults] = useState<ChannelDiscovery[]>([])
  const [addingChannels, setAddingChannels] = useState<Set<number>>(new Set())

  // Search mutation
  const searchMutation = useMutation({
    mutationFn: () => channelsService.discoverChannels(query, minSubscribers),
    onSuccess: (data) => {
      setResults(data)
      if (data.length === 0) {
        toast('Каналы не найдены', { icon: 'ℹ️' })
      }
    },
    onError: () => {
      toast.error('Ошибка при поиске каналов')
    },
  })

  // Add channel mutation
  const addChannelMutation = useMutation({
    mutationFn: (username: string) =>
      channelsService.addChannel({ username }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      toast.success('Канал добавлен')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) {
      toast.error('Введите поисковый запрос')
      return
    }
    searchMutation.mutate()
  }

  const handleAddChannel = async (channel: ChannelDiscovery) => {
    if (!channel.username) {
      toast.error('У канала нет публичного username')
      return
    }

    setAddingChannels((prev) => new Set(prev).add(channel.telegram_channel_id))

    try {
      await addChannelMutation.mutateAsync(channel.username)
      // Remove from results
      setResults((prev) =>
        prev.filter((c) => c.telegram_channel_id !== channel.telegram_channel_id)
      )
    } finally {
      setAddingChannels((prev) => {
        const next = new Set(prev)
        next.delete(channel.telegram_channel_id)
        return next
      })
    }
  }

  const formatNumber = (num: number | null) => {
    if (num === null) return '—'
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Поиск каналов
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Найдите релевантные каналы по вашей нише
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="px-6 py-4 border-b border-gray-200">
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Введите тему или ключевые слова..."
                className="input flex-1"
              />
              <button
                type="submit"
                disabled={searchMutation.isPending}
                className="btn-primary"
              >
                {searchMutation.isPending ? 'Поиск...' : 'Искать'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500 whitespace-nowrap">Мин. подписчиков:</label>
              <input
                type="number"
                value={minSubscribers || ''}
                onChange={(e) => setMinSubscribers(Number(e.target.value) || 0)}
                placeholder="0"
                min={0}
                className="input w-28 text-sm"
              />
            </div>
          </form>

          {/* Results */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {results.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchMutation.isPending
                  ? 'Поиск каналов...'
                  : 'Введите запрос для поиска каналов'}
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((channel) => (
                  <div
                    key={channel.telegram_channel_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 truncate">
                          {channel.title}
                        </h4>
                        <span
                          className={`px-2 py-0.5 text-xs rounded ${
                            channel.relevance_score >= 0.8
                              ? 'bg-green-100 text-green-700'
                              : channel.relevance_score >= 0.5
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {Math.round(channel.relevance_score * 100)}%
                        </span>
                      </div>
                      {channel.username && (
                        <p className="text-sm text-blue-600">@{channel.username}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="font-semibold text-gray-700 text-sm">
                          👥 {formatNumber(channel.member_count)}
                        </span>
                        {channel.channel_type && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            channel.channel_type === 'group'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {channel.channel_type === 'group' ? 'Чат' : 'Канал'}
                          </span>
                        )}
                        {channel.description && (
                          <span className="truncate">{channel.description}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddChannel(channel)}
                      disabled={
                        addingChannels.has(channel.telegram_channel_id) ||
                        !channel.username
                      }
                      className="ml-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addingChannels.has(channel.telegram_channel_id)
                        ? '...'
                        : '+ Добавить'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
